// --- React ---
import { useState, useEffect } from "react";
import { useLocation, useSearchParams, useParams } from "react-router-dom";

// --- Services ---
import samplingScheduleSvc from "../../services/api/samplingSchedules";
import stationSvc from "../../services/api/station";

// --- Models ---
import { SamplingScheduleItem, SamplingScheduleFilter } from "../../models/types/samplingSchedules";
import { DefaultFilterRequest, PaginationMeta } from "../../models/types/common";
import { StationItem } from "../../models/types/station";

// --- Components ---
import ListTable from "../../components/list-table";
import Pagination from "../../components/pagination";
import { notify } from "../../common/notificationService";
import ButtonAction from "../../components/button/buttonAction";
import { useNavigate } from "react-router-dom";
import { UrlCollection } from "../../common/url-collection";
import ModalConfirm from "../../components/modal-comfirm";

// --- Icons ---
import { Trash2, Pencil, ArrowLeft } from "lucide-react";

// --- Utils ---
import dayjs from "dayjs";

export default function ParameterUnitPage() {
    const navigate = useNavigate();
    const { stationId } = useParams<{ stationId?: string }>();

    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    const [data, setData] = useState<SamplingScheduleItem[]>([]);
    const [station, setStation] = useState<StationItem | null>(null);
    const [pagination, setPagination] = useState<PaginationMeta>({});
    const [orderBy, setOrderBy] = useState<string>("modifiedDate");
    const [order, setOrder] = useState<"asc" | "desc">("desc");
    const [loading, setLoading] = useState<boolean>(false);
    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<SamplingScheduleItem | null>(null);

    const getStationById = async (id: string) => {
        try {
            const res = await stationSvc.getStationById(id);
            if (res && res.content) {
                setStation(res.content);
            }
        } catch (error) {
            console.error(error);
            notify("Lỗi", "Xảy ra lỗi", "danger");
        }
    };

    const getListAll = async (params: DefaultFilterRequest<SamplingScheduleFilter>) => {
        setLoading(true);
        try {
            const res = await samplingScheduleSvc.getAll(params);
            if (res && res.content) {
                const { items, pageCount, totalItemCount, pageIndex, pageSize } = res.content;
                setData(items);
                setPagination({ pageCount, totalItemCount, pageIndex, pageSize });
            }
        } catch (error) {
            console.error(error);
            setData([]);
            setPagination({});
            notify("Lỗi", "Xảy ra lỗi", "danger");
        } finally {
            setLoading(false);
        }
    };

    const onDelete = async (row: SamplingScheduleItem) => {
        try {
            const res = await samplingScheduleSvc.deleteSamplingSchedule(row.id);
            if (res && res.content.status) {
                notify("Thành công", "Xóa tham số thành công", "success");
                fetchData();
            }
        } catch (error) {
            console.error(error);
            notify("Lỗi", "Xảy ra lỗi", "danger");
        }
    };

    const fetchData = async () => {
        const queryParams = new URLSearchParams(location.search);
        const params: DefaultFilterRequest<SamplingScheduleFilter> = {
            stationId: stationId!,
            sorting: queryParams.get("sorting"),
            pageIndex: queryParams.get("pageIndex") || "1",
            pageSize: queryParams.get("pageSize") || "20",
            isDelete: queryParams.get("isDelete") || "false",
        };

        await getListAll(params);
    };

    useEffect(() => {
        getStationById(stationId!);
    }, [stationId]);

    useEffect(() => {
        fetchData();
    }, [location.search]);

    const handlePageChange = (page: number) => {
        searchParams.set("pageIndex", page.toString());
        setSearchParams(searchParams);
    };

    const handleChangePageSize = (size: number) => {
        searchParams.set("pageSize", size.toString());
        searchParams.set("pageIndex", "1"); // reset về trang đầu tiên
        setSearchParams(searchParams);
    };

    const onRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === "asc";
        const sort = isAsc ? "desc" : "asc";
        setOrder(sort);
        setOrderBy(property);

        searchParams.set("sorting", `${property} ${sort}`);
        setSearchParams(searchParams);
    };

    const handleEdit = (row: SamplingScheduleItem) => {
        navigate(UrlCollection.EditSamplingSchedule.replace(":stationId", stationId!.toString()).replace(":id", row.id.toString()));
    };

    const handleDelete = (row: SamplingScheduleItem) => {
        setSelectedRow(row);
        setOpenModalConfirm(true);
    };

    const columns = [
        { header: "STT", accessor: "index", sortable: false, className: "max-w-[50px]" },
        {
            header: "Loại",
            accessor: "scheduleType",
            sortable: true,
        },
        {
            header: "Khoảng thời gian (phút)",
            accessor: "intervalMinutes",
            sortable: true,
            className: "min-w-[90px]",
        },
        {
            header: "Bắt đầu lấy mẫu",
            accessor: (row: SamplingScheduleItem) => `${dayjs(row.createdAt).format("DD/MM/YYYY HH:mm:ss")}`,
            sortable: true,
        },
        {
            header: "Kết thúc lấy mẫu",
            accessor: (row: SamplingScheduleItem) => `${dayjs(row.lastRunAt).format("DD/MM/YYYY HH:mm:ss")}`,
            sortable: true,
        },
        {
            header: "Ngưỡng tối thiểu",
            accessor: "thresholdMin",
            sortable: true,
        },
        {
            header: "Ngưỡng tối đa",
            accessor: "thresholdMax",
            sortable: true,
        },
        {
            header: "Trạng thái",
            accessor: (row: SamplingScheduleItem) => (
                <span className={`badge ${row.isActive ? "badge-success" : "badge-error"}`}>
                    {row.isActive ? "Hoạt động" : "Ngừng hoạt động"}
                </span>
            ),
            sortable: true,
        },
        {
            header: "",
            accessor: (row: SamplingScheduleItem) => (
                <div className="flex gap-2">

                    <ButtonAction
                        onClick={() => handleEdit(row)}
                        icon={<Pencil size={14} />}
                        color="blue"
                        title="Edit"
                    />

                    <ButtonAction
                        onClick={() => handleDelete(row)}
                        icon={<Trash2 size={14} />}
                        color="red"
                        title="Delete"
                    />
                </div>
            ),
            sortable: false,
            className: "max-w-[100px]",
        },
    ];

    return (
        <div className="page-wrapper list flex flex-col">
            <div className="page-header">
                <div className="flex items-center gap-4">
                    <button
                        className="!py-3 !px-4 btn shadow-sm text-sm font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowLeft size={14} className=""/>
                    </button>
                    <div className="flex flex-col">
                        <h1 className="text-xl font-semibold">Danh sách lịch lấy mẫu {station?.name}</h1>
                        <p className="text-sm text-gray-500">{station?.address}</p>
                    </div>
                </div>
                <button
                    className="btn shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    onClick={() => navigate(UrlCollection.CreateSamplingSchedule.replace(":stationId", stationId!.toString()))}
                >
                    Thêm mới
                </button>
            </div>
            <div className="my-2">
            </div>
            <ListTable
                columns={columns}
                data={data}
                isLoading={loading}
                sortBy={orderBy}
                order={order}
                handleSort={onRequestSort}
            />
            {pagination.totalItemCount ? (
                <Pagination
                    pageCount={pagination.pageCount}
                    totalItemCount={pagination.totalItemCount}
                    pageIndex={pagination.pageIndex}
                    pageSize={pagination.pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handleChangePageSize}
                />
            ) : null}
            {openModalConfirm && (
                <ModalConfirm
                    isOpen={openModalConfirm}
                    onClose={() => setOpenModalConfirm(false)}
                    title="Xác nhận"
                    content="Bạn có chắc chắn muốn xóa tham số này?"
                    titleComfirm="Xác nhận"
                    action={() => {
                        onDelete(selectedRow!);
                        setOpenModalConfirm(false);
                    }}
                />
            )}
        </div>
    );
}
